import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import {
  AccountBalanceWallet,
  Close,
  Warning,
  CheckCircle
} from '@mui/icons-material';
import { useWeb3React } from '@web3-react/core';
import { toast } from 'react-toastify';
import { metaMask, walletConnect, coinbaseWallet } from '../config/connectors';
import { NETWORKS, isSupportedNetwork } from '../config/contracts';

interface WalletConnectionProps {
  onConnect?: () => void;
}

const WalletConnection: React.FC<WalletConnectionProps> = ({ onConnect }) => {
  const { connector, account, chainId, isActive } = useWeb3React();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingWallet, setConnectingWallet] = useState<string>('');

  const isSupported = chainId ? isSupportedNetwork(chainId) : false;
  const networkName = chainId === NETWORKS.POLYGON_MAINNET.chainId ? 'Polygon Mainnet' : 
                    // chainId === NETWORKS.POLYGON_AMOY.chainId ? 'Polygon Amoy Testnet' : 
                     'Unknown Network';

  // Connect wallet
  const connectWallet = async (walletType: 'metamask' | 'walletconnect' | 'coinbase') => {
    setIsConnecting(true);
    setConnectingWallet(walletType);
    
    try {
      let connector;
      let walletName;
      
      switch (walletType) {
        case 'metamask':
          connector = metaMask;
          walletName = 'MetaMask';
          break;
        case 'walletconnect':
          connector = walletConnect;
          walletName = 'WalletConnect';
          break;
        case 'coinbase':
          connector = coinbaseWallet;
          walletName = 'Coinbase Wallet';
          break;
        default:
          throw new Error('Unsupported wallet type');
      }
      
      await connector.activate();
      setIsModalOpen(false);
      toast.success(`Connected to ${walletName}`);
      onConnect?.();
    } catch (err: any) {
      console.error('Failed to connect wallet:', err);
      let errorMessage = 'Failed to connect wallet. Please make sure your wallet is installed and unlocked';
      if (err.code === 4001) {
        errorMessage = 'User rejected the connection request';
      } else if (err.code === -32002) {
        errorMessage = 'Please handle the pending connection request in your wallet';
      }
      toast.error(errorMessage);
    } finally {
      setIsConnecting(false);
      setConnectingWallet('');
    }
  };

  // Disconnect
  const disconnect = () => {
    if (connector?.deactivate) {
      connector.deactivate();
    } else {
      connector.resetState();
    }
    toast.info('Wallet disconnected');
  };

  // Switch network
  const handleSwitchNetwork = async (targetChainId: number) => {
    try {
      if (window.ethereum) {
        await (window.ethereum as any).request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${targetChainId.toString(16)}` }],
        });
        toast.success('Network switched successfully');
      }
    } catch (err: any) {
      console.error('Failed to switch network:', err);
      let errorMessage = 'Failed to switch network. Please switch manually in MetaMask';
      if (err.code === 4001) {
        errorMessage = 'User rejected the network switch request';
      } else if (err.code === 4902) {
        errorMessage = 'Please add the network to MetaMask first';
      }
      toast.error(errorMessage);
    }
  };

  // Format address
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (!isActive) {
    return (
      <>
        <Button
          variant="contained"
          startIcon={<AccountBalanceWallet />}
          onClick={() => setIsModalOpen(true)}
          sx={{ minWidth: 140 }}
        >
          Connect Wallet
        </Button>

        <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Connect Wallet</Typography>
              <IconButton onClick={() => setIsModalOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a wallet to connect to the application
            </Typography>
            
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AccountBalanceWallet />}
              onClick={() => connectWallet('metamask')}
              disabled={isConnecting}
              sx={{ mb: 2, py: 1.5 }}
            >
              {isConnecting && connectingWallet === 'metamask' ? 'Connecting...' : 'MetaMask'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<AccountBalanceWallet />}
              onClick={() => connectWallet('walletconnect')}
              disabled={isConnecting}
              sx={{ mb: 2, py: 1.5 }}
            >
              {isConnecting && connectingWallet === 'walletconnect' ? 'Connecting...' : 'WalletConnect'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<AccountBalanceWallet />}
              onClick={() => connectWallet('coinbase')}
              disabled={isConnecting}
              sx={{ mb: 2, py: 1.5 }}
            >
              {isConnecting && connectingWallet === 'coinbase' ? 'Connecting...' : 'Coinbase Wallet'}
            </Button>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Please ensure your wallet is installed and unlocked. Supported wallets: MetaMask, WalletConnect and Coinbase Wallet. Supported networks: Polygon Mainnet and Polygon Amoy Testnet.
              </Typography>
            </Alert>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      {/* Network status */}
      <Chip
        icon={isSupported ? <CheckCircle /> : <Warning />}
        label={networkName}
        color={isSupported ? 'success' : 'warning'}
        size="small"
      />
      
      {/* Account info */}
      <Chip
        icon={<AccountBalanceWallet />}
        label={account ? formatAddress(account) : 'Not Connected'}
        color="primary"
        variant="outlined"
      />
      
      {/* Network switch button */}
      {!isSupported && (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          onClick={() => handleSwitchNetwork(NETWORKS.POLYGON_MAINNET.chainId)}
        >
          Switch to POLYGON_MAINNET
        </Button>
      )}
      
      {/* Disconnect button */}
      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={disconnect}
      >
        Disconnect
      </Button>
    </Box>
  );
};

export default WalletConnection;